import React, { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useEa } from "@/components/energy-analytics/analytics/EaContext";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { EaChartCard, EaField, EaKpiCard, EaNote, EaPageHeader, EaSeg, inputCls } from "@/components/energy-analytics/analytics/EaUi";
import { EaExportButton, EaPointPicker, EaTable, TOT, useSelectedCodes } from "@/components/energy-analytics/analytics/EaControls";
import { COLORS, MONTHS_FULL, MONTHS_IT, fmtEur, fmtPower, fmtQty } from "@/components/energy-analytics/analytics/eaFormat";
import { PageState } from "./EaPowerOverview";
import { EaGuideButton, EaGuidePanel, EaPresetButtons } from "@/components/energy-analytics/analytics/EaGuide";
import { toast } from "sonner";

const axis = { tick: { fontSize: 11, fill: "#64748B" }, axisLine: false, tickLine: false };

function HourSel({ value, onChange, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))} className={`${inputCls} w-24`} data-testid={testid}>
      {Array.from({ length: 25 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
    </select>
  );
}

function BeforeAfter({ label, before, after, fmt, testid, lowerIsBetter = true }) {
  const better = before != null && after != null && (lowerIsBetter ? after < before : after > before);
  return (
    <EaKpiCard label={label} testid={testid} value={<span>{fmt(after)}</span>}
      sub={<span>Prima {fmt(before)} · <span className={better ? "text-green-700 font-semibold" : "text-slate-500"}>{before != null && after != null ? `${after - before > 0 ? "+" : ""}${fmt(after - before)}` : ""}</span></span>} />
  );
}

export default function EaPowerLoadShift() {
  const { filters, setFilter, nav } = useEa();
  const codes = useSelectedCodes("POWER");
  const month = filters.month || "";
  const [share, setShare] = useState(10);
  const [shareQ, setShareQ] = useState(10);
  const [mode, setMode] = useState("auto");
  const [win, setWin] = useState({ src_from: 8, src_to: 12, tgt_from: 13, tgt_to: 16 });
  const [guideOpen, setGuideOpen] = useState(false);
  const applyPreset = (p) => {
    const v = p.values;
    setShare(v.share_pct); setShareQ(v.share_pct); setMode(v.mode);
    if (v.mode === "manual") setWin({ src_from: v.src_from, src_to: v.src_to, tgt_from: v.tgt_from, tgt_to: v.tgt_to });
    toast.info(`Scenario "${p.label}" applicato`);
  };
  useEffect(() => { const t = setTimeout(() => setShareQ(share), 400); return () => clearTimeout(t); }, [share]);
  const extra = { codes, month, share_pct: shareQ, ...(mode === "manual" ? win : {}) };
  const { data: d, loading, error } = useEaData("/ea/power/load-shift", extra);
  const header = (
    <EaPageHeader title="Simulazione spostamento carichi" subtitle="Quanto si risparmierebbe spostando una quota di consumi dalle ore più care a quelle più economiche della stessa giornata."
      right={<div className="flex gap-2 flex-wrap"><select value={month} onChange={(e) => setFilter({ month: e.target.value })} className={inputCls} data-testid="ea-ls-month">
        <option value="">Tutto l'anno</option>{MONTHS_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select><EaPointPicker commodity="POWER" /><EaGuideButton onClick={() => setGuideOpen(true)} /></div>} />
  );
  const controls = (
    <div className="card-elevated p-4 flex flex-wrap items-end gap-6" data-testid="ea-ls-controls">
      <div className="w-full"><EaPresetButtons guideKey="load-shift" onApply={applyPreset} label="Scenari pronti:" testid="ea-ls-presets" /></div>
      <EaField label={`Quota da spostare: ${share}%`}>
        <input type="range" min="0" max="50" step="1" value={share} onChange={(e) => setShare(Number(e.target.value))} className="w-64 accent-[#0B4DFF]" data-testid="ea-ls-share" />
      </EaField>
      <EaField label="Scelta delle ore"><EaSeg value={mode} onChange={setMode} options={[["auto", "Automatica (20% più care → 20% più economiche del giorno)"], ["manual", "Fasce scelte"]]} testid="ea-ls-mode" /></EaField>
      {mode === "manual" && (
        <div className="flex items-end gap-3" data-testid="ea-ls-windows">
          <EaField label="Da (origine)"><div className="flex items-center gap-1"><HourSel value={win.src_from} onChange={(v) => setWin({ ...win, src_from: v })} testid="ea-ls-src-from" />–<HourSel value={win.src_to} onChange={(v) => setWin({ ...win, src_to: v })} testid="ea-ls-src-to" /></div></EaField>
          <EaField label="A (destinazione)"><div className="flex items-center gap-1"><HourSel value={win.tgt_from} onChange={(v) => setWin({ ...win, tgt_from: v })} testid="ea-ls-tgt-from" />–<HourSel value={win.tgt_to} onChange={(v) => setWin({ ...win, tgt_to: v })} testid="ea-ls-tgt-to" /></div></EaField>
        </div>
      )}
    </div>
  );
  const t = d?.total;
  const pCols = [{ key: "identifier", label: "POD", align: "left" }, { key: "shifted_kwh", label: "kWh spostati", fmt: (v) => fmtQty(v, 0), total: "sum" },
    { key: "blocked_kwh", label: "kWh non spostabili (limite potenza)", fmt: (v) => fmtQty(v, 0), total: "sum" }, { key: "saving_eur", label: "Risparmio teorico €", fmt: (v) => fmtEur(v), total: "sum" },
    { key: "peak_before_kw", label: "Picco prima kW", fmt: (v) => fmtQty(v, 0) }, { key: "peak_after_kw", label: "Picco dopo kW", fmt: (v) => fmtQty(v, 0) },
    { key: "available_power_kw", label: "Potenza disp. kW", fmt: (v) => fmtQty(v, 0) }];
  return (
    <div className="space-y-6" data-testid="ea-power-load-shift">
      {header}
      <EaGuidePanel guideKey="load-shift" open={guideOpen} setOpen={setGuideOpen} onApply={applyPreset} />
      {controls}
      <PageState header={null} loading={loading} error={error} data={d} testid="ea-ls-results">
        {d && (!d.has_pun ? <EaNote testid="ea-ls-nopun">PUN o curve di consumo non disponibili.</EaNote> : <>
          {nav?.availability?.pun_granularity === "HOURLY" && <EaNote testid="ea-ls-pun-hourly">Nel {d.year} il PUN era solo orario: la simulazione valorizza l'energia spostata con il PUN orario.</EaNote>}
          <div className="card-elevated p-4 border-l-4 !border-l-amber-400 bg-amber-50/60 text-sm text-amber-900" data-testid="ea-ls-disclaimer">
            Risparmio <b>teorico sul PUN</b>: non comprende spread, oneri, perdite né il costo operativo dello spostamento. Il consumo di ogni giornata resta invariato e l'energia spostata non porta nessun quarto d'ora oltre la potenza disponibile.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <EaKpiCard label="Risparmio teorico sul PUN" value={<span className="text-green-700">{fmtEur(t.saving_eur)}</span>} testid="ea-ls-kpi-saving"
              sub={`${fmtQty(t.saving_per_mwh, 2, "€/MWh")} sul consumo totale (${fmtPower(t.consumption_kwh, "MWh")})`} />
            <EaKpiCard label="Energia spostata" value={fmtPower(t.shifted_kwh, "MWh")} testid="ea-ls-kpi-shifted"
              sub={t.blocked_kwh > 0 ? `${fmtPower(t.blocked_kwh, "MWh")} non spostabili per il limite di potenza` : "Tutta la quota richiesta è stata spostata"} />
            <BeforeAfter label="PUN ponderato cliente" before={t.before.pun_weighted} after={t.after.pun_weighted} fmt={(v) => fmtQty(v, 2, "€/MWh")} testid="ea-ls-kpi-pun" />
            <BeforeAfter label="Consumo nelle ore più care" before={t.before.share_expensive_pct} after={t.after.share_expensive_pct} fmt={(v) => fmtQty(v, 1, "%")} testid="ea-ls-kpi-share" />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <EaChartCard title="Profilo orario medio prima / dopo" subtitle="kWh medi per ora" testid="ea-ls-card-hourly">
              <div className="h-72" data-testid="ea-ls-chart-hourly">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={d.hourly.map((h) => ({ ...h, label: String(h.hour).padStart(2, "0") }))}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis dataKey="label" {...axis} />
                    <YAxis {...axis} width={56} tickFormatter={(v) => fmtQty(v, 0)} />
                    <Tooltip formatter={(v, n) => [fmtQty(v, 0, "kWh"), n]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line dataKey="before" name="Prima" stroke="#94A3B8" strokeWidth={2} dot={false} />
                    <Line dataKey="after" name="Dopo lo spostamento" stroke={COLORS.power} strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </EaChartCard>
            {!month && (
              <EaChartCard title="Risparmio teorico per mese" testid="ea-ls-card-monthly" right={
              <EaExportButton filename={`power_simulazione_mensile_${d.year}`} commodity="POWER" year={d.year} testid="ea-ls-export-monthly"
                columns={[{ key: "month", label: "Mese" }, { key: "share", label: "Quota spostata %" }, { key: "saving_eur", label: "Risparmio teorico PUN €" }]}
                rows={d.monthly.map((m) => ({ ...m, share: d.share_pct }))} />
              }>
                <div className="h-72" data-testid="ea-ls-chart-monthly">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={d.monthly.map((m) => ({ ...m, label: MONTHS_IT[m.month - 1] }))}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                      <XAxis dataKey="label" {...axis} />
                      <YAxis {...axis} width={60} tickFormatter={(v) => fmtQty(v, 0)} />
                      <Tooltip formatter={(v) => [fmtEur(v), "Risparmio teorico"]} />
                      <Bar dataKey="saving_eur" fill="#16A34A" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </EaChartCard>
            )}
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-display font-semibold text-slate-900">Dettaglio per POD</div>
            <div className="flex gap-2 flex-wrap">
              <EaExportButton filename={`power_simulazione_pod_${d.year}`} commodity="POWER" year={d.year} testid="ea-ls-export-points"
                period={month ? `${MONTHS_FULL[month - 1]} ${d.year}` : `Anno ${d.year}`} columns={pCols} rows={d.points} />
            </div>
          </div>
          <EaTable columns={pCols} rows={d.points} testid="ea-ls-table" />
        </>)}
      </PageState>
    </div>
  );
}
